/* RouteGuard — redirects to /login when there is no session.
 *
 * Uses react-router's declarative <Navigate replace>. Per
 * saas-greenfield-pitfalls §3: this is the React equivalent of
 * `throw redirect(307, ...)` — the protected children never mount,
 * so no flicker and no protected-render side effects.
 *
 * If we migrate to react-router data routers, this can move to a
 * loader function with `throw redirect('/login')`. */

import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';

interface RouteGuardProps {
  children?: ReactNode;
}

export function RouteGuard({ children }: RouteGuardProps): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // When used as a layout element (no `children` passed), render <Outlet/>;
  // otherwise render `children` directly.
  return children ? <>{children}</> : <Outlet />;
}
